/**
 * Design tokens — derived from Google Material 3 + Web Vitals/UX research.
 *
 * Type scale: Material 3 (https://m3.material.io/styles/typography/type-scale-tokens)
 * Spacing: 4pt grid (Material 3 layout)
 * Radii: bookings benefit from softer, rounded surfaces (M3 "large")
 * Motion: 100-250ms (M3 "short"/"medium" tokens, INP-friendly)
 * Touch: 48dp minimum target (Material accessibility, Apple HIG 44pt)
 *
 * Color blending via CSS `color-mix(in srgb, …)` rather than hex+opacity hacks —
 * gives proper sRGB mixing and works with any tenant primary color.
 */

export const RADII = {
  surface: 'rounded-3xl', // 24px — step shell
  card: 'rounded-2xl', // 16px — inner surfaces
  control: 'rounded-full', // pill — buttons, chips
  field: 'rounded-xl', // 12px — inputs
} as const;

export const TYPE = {
  // Material 3 Title scale — used for step titles
  titleLg: 'text-[22px] font-semibold leading-7 tracking-tight',
  titleMd: 'text-base font-semibold leading-6 tracking-tight',
  titleSm: 'text-sm font-semibold leading-5',
  // Body — primary readable text
  bodyLg: 'text-base leading-6',
  bodyMd: 'text-sm leading-5',
  bodySm: 'text-xs leading-4',
  // Label — overline / metadata
  labelLg: 'text-sm font-medium leading-5',
  labelMd: 'text-xs font-medium leading-4',
  labelSm: 'text-[11px] font-semibold uppercase tracking-[0.08em] leading-4',
} as const;

export const MOTION = {
  fast: 'duration-150',
  base: 'duration-200',
  slow: 'duration-300',
} as const;

export const TOUCH = {
  target: 'min-h-[44px]', // 44px iOS / 48dp Material approximation
  comfort: 'min-h-[48px]',
} as const;

/**
 * Build a themed primary color background using color-mix.
 * Example: themedBg('#10b981', 12) -> 12% tinted background
 */
export function themedTint(color: string, percent: number): string {
  return `color-mix(in srgb, ${color} ${percent}%, transparent)`;
}

/**
 * Like themedTint, but blends the accent toward the surface INK (`--sf-text`)
 * instead of transparent — so the result is a solid colour that keeps a minimum
 * contrast against the surface in BOTH light and dark, even for extreme tenant
 * accents (near-white primary on a light bg, near-black on dark). Use for small
 * FOREGROUND signals that must stay visible regardless of accent (e.g. the
 * calendar today-ring / availability dots), not for subtle background tints.
 *
 * `percent` = how much accent to keep; the remainder is surface ink.
 */
export function themedContrast(color: string, percent: number): string {
  return `color-mix(in srgb, ${color} ${percent}%, var(--sf-text, #111827))`;
}

/**
 * Darken a themed color for hover state.
 */
export function themedHover(color: string, mixWithBlack: number = 12): string {
  return `color-mix(in srgb, ${color} ${100 - mixWithBlack}%, black)`;
}
